/**
 * WHAT THE HOW-TO-PLAY MODAL HAS PAGES FOR — derived, not listed.
 *
 * Pure and DOM-free, for `hud/duel-pot.ts`' and `hud/saying-lines.ts`' stated reason: which
 * pages exist and what each one is about are things to assert, and the modal they are drawn in
 * needs a DOM to say anything at all.
 *
 * ## THE MINI-GAME PAGES ARE GENERATED FROM `MINI_GAMES`
 *
 * There are two fixed pages — the gameplay one this modal has always been, and the power-ups one
 * that already read `POWER_UPS` rather than prose — and then **one page per entry in the
 * mini-game registry**, in the registry's own order. A third mini-game is a file in
 * `packages/shared/src/minigames` plus one line in `MINI_GAMES`, exactly as it is for the duel
 * overlay's buttons and for `duel-pot.ts`' dial, and it gets a page here with no edit to this
 * file or to the component that draws it.
 *
 * A hand-written list of games is precisely the drift this codebase keeps designing out, and it
 * is the failure that would not fail loudly: a registered game with no page is playable and
 * undocumented, and nothing breaks. So the list is a `map` over the registry, and the test that
 * holds it registers a fixture game and asserts a page appears.
 *
 * `games` is a parameter with the registry as its default for the same reason `DuelOverlay`
 * takes `now`: it is the one input a test has to control to check the derivation rather than the
 * two entries that happen to be registered today. Production never passes it.
 *
 * ## AND THE STAR IS A THIRD HAND-DECLARED KIND, WHICH IS THE DECISION THIS FILE MAKES
 *
 * The player: "the instructions have chicken but they don't mention anything about the star I
 * don't believe." They were right, and the reason is structural rather than an oversight — the
 * page list was two fixed kinds plus `MINI_GAMES`, and **a star is none of those**. It is a room
 * event with a pickup, a vote and a gamble; no duel runs it and `pickMiniGame` can never return
 * it.
 *
 * So it is declared here beside `gameplay` and `powerups`, and it is emphatically **not** faked
 * as a registry entry. A `MiniGame` that no duel could ever run would put a fourth thing in
 * `MINI_GAMES` for `pickMiniGame` to draw, break every test that asserts the registry's own
 * shape, and offer a player a page implying they will one day be frozen into a star. The two
 * fixed pages exist precisely because not everything worth teaching is a registry entry; this is
 * the third.
 *
 * **Where it sits is after Power-ups and before the games**, which is the same reading order the
 * rest of the list is in: gameplay is how you play at all, an orb is the next thing you meet on
 * the map, a star is the rarest thing on that same map — and a duel is the thing that happens
 * *to* you. Putting it among the mini-games would say a duel runs it, which is the one wrong
 * thing this page could imply.
 */

import {
  DUEL_DECIDE_TICKS,
  STAR_VOTE_OPTIONS,
  STAR_VOTE_PROMPT,
  STARTING_MASS,
  TICK_RATE_HZ,
  VOTE_DURATION_SECONDS,
} from '@chompdome/shared/constants';
import {
  MINI_GAME_POT_MAX_PERCENT,
  type MiniGame,
  MINI_GAMES,
  type MiniGamePot,
  miniGamePotOf,
  miniGamePotRungs,
} from '@chompdome/shared/minigames';
import type { DuelView, VoteView } from '@/game/net/connection';

/** The three pages that are not a mini-game. `minigame` is every other page. */
export type InstructionPageKind = 'gameplay' | 'powerups' | 'star' | 'minigame';

export interface InstructionPage {
  /**
   * Stable within a render, and the value the nav selects on.
   *
   * A mini-game's page is namespaced (`game:<id>`) rather than keyed on the bare registry id,
   * so a future game called `gameplay` or `powerups` cannot collide with a fixed page.
   */
  readonly id: string;
  /** What the nav chip says. A mini-game's own `name`, so the page and the duel card agree. */
  readonly label: string;
  readonly kind: InstructionPageKind;
  /** The game this page is about, for a `minigame` page and never otherwise. */
  readonly game?: MiniGame;
}

/** The page id a mini-game gets. Exported so a test names a page the way the modal does. */
export function miniGamePageId(game: MiniGame): string {
  return `game:${game.id}`;
}

/**
 * Every page the modal has, in reading order.
 *
 * Gameplay first because it is the answer to "how do I play at all", power-ups second because
 * they are the next thing a player meets on the map, the star third because it is the rarest
 * thing on that same map, and the mini-games after all three because a duel is the thing that
 * happens *to* you — by the time somebody is reading about chicken they have already been frozen
 * into one.
 */
export function instructionPages(
  games: readonly MiniGame[] = MINI_GAMES,
): readonly InstructionPage[] {
  return [
    { id: 'gameplay', label: 'Gameplay', kind: 'gameplay' },
    { id: 'powerups', label: 'Power-ups', kind: 'powerups' },
    { id: 'star', label: 'Star', kind: 'star' },
    ...games.map(
      (game): InstructionPage => ({
        id: miniGamePageId(game),
        label: game.name,
        kind: 'minigame',
        game,
      }),
    ),
  ];
}

/**
 * The climb the instructions page freezes a pot's bar at.
 *
 * **DERIVED FROM THE CURVE, NEVER A NUMBER TYPED HERE.** The bar the page mounts is the real
 * `DuelPotBar` and the example has to stay right when a game's bands move, so the percentage is
 * the pot's own **top rung** — the highest place its ladder is worth reaching, found by walking
 * `share()` (`miniGamePotRungs`). For chicken that lands at 80%, which is the single most useful
 * frame there is: past the bust floor, so the fill is in its danger colour and the caption says
 * so, with every rung below it already reached and the whole ladder legible above it.
 *
 * A pot with no rungs at all — a flat climb — has no interesting point, so it is shown halfway
 * up. That is the degenerate case a real game is measured against rather than one any registered
 * game hits.
 */
export function instructionPotPercent(pot: MiniGamePot): number {
  const rungs = miniGamePotRungs(pot);
  const top = rungs.at(-1);
  return top === undefined ? MINI_GAME_POT_MAX_PERCENT / 2 : top.percent;
}

/**
 * The mass the star page's example card is drawn against.
 *
 * **IT IS A SAMPLE PLAYER RATHER THAN A CONSTANT OF THE GAME, AND SAYING SO IS THE POINT.** What
 * the star's card actually prints is `STAR_GAMBLE_PAYOUT_MULTIPLIER` times *your* mass, which is
 * a different number for every reader and for every second of their run; a page has to pick
 * somebody to be. So this is anchored to `STARTING_MASS` rather than typed as a bare number — it
 * moves with the spawn size instead of quietly becoming a strange figure the day that does — and
 * it is an order of magnitude above it because a fresh spawn has nothing to lose and the whole
 * decision this page is about is a decision about a run worth keeping.
 *
 * Nothing derives a *rule* from it: the payout beside it is `starGamblePayout` of this, never a
 * second multiplication written on the page, so the example follows the constant rather than
 * agreeing with it by hand.
 */
export const INSTRUCTION_STAR_SAMPLE_MASS = STARTING_MASS * 10;

/**
 * The star page's example vote: the real `VoteView` shape, filled from the real constants.
 *
 * The prompt and the ballot are `STAR_VOTE_PROMPT` and `STAR_VOTE_OPTIONS` — the very values
 * `RoomSim.openVote` copies when a star is taken, so the card on this page asks exactly the
 * question a room asks — and the deadline is `VOTE_DURATION_SECONDS` out from whatever instant
 * the page hands in. **`rocket-pack.ts`' bargain, one surface along:** a page that spelled its own
 * "HEADS / TAILS" or its own ten seconds would be wrong the first time either moved, which is
 * precisely what the card ahead of this one did to the star's spawn rate.
 *
 * `star` is true because that boolean is the whole reason this card exists rather than the
 * ordinary ballot, and `canAnswer` is true because the reader is being shown the control they
 * will actually get — a disabled one is the spectator's reading and would teach the wrong thing.
 * `result` and `self` are null: this is an *open* vote, and an open vote carries no tally, which
 * is the wire's own rule and not a simplification made here.
 *
 * `now` is a parameter rather than a `Date.now()` inside, for `DuelOverlay`'s reason: the one
 * input a test has to control.
 */
export function instructionStarVote(now: number, choice: string | null = null): VoteView {
  return {
    id: 'v-example',
    prompt: STAR_VOTE_PROMPT,
    options: STAR_VOTE_OPTIONS,
    endsAtMs: now + VOTE_DURATION_SECONDS * 1000,
    yourChoice: choice,
    // The reader's tap is on their own screen the instant it happens and there is no room to
    // have heard it, so the example says SENT the moment it is pressed — which is what the card
    // says in game between the tap and the input frame leaving.
    answerSent: choice !== null,
    canAnswer: true,
    star: true,
    result: null,
    self: null,
  };
}

/**
 * The opponent the instructions page duels, and the stake it plays for.
 *
 * A name rather than a blank, because `DuelCard` prints "vs {opponentName}" in its header and an
 * empty one would read as a bug. It is deliberately not a real player's: nothing on this page is
 * connected to a room.
 *
 * The stake is **null**, which is not an omission — it is how this client tells a duel the two of
 * you drove into from one a duel gun started (`DuelView.stake`), and a contact duel stakes a share
 * of whoever ends up losing rather than a number fixed up front. So the example is the ordinary
 * duel a reader will meet by colliding with somebody their own size, and the card draws no
 * "Playing for N mass" line, exactly as it would not in that duel.
 */
export const INSTRUCTION_DUEL_OPPONENT = 'Wobble';

/**
 * How long the example's duel runs, in browser milliseconds.
 *
 * `DUEL_DECIDE_TICKS / TICK_RATE_HZ` and never a number typed here: for a pot game the climb and
 * the answering window are the *same* clock (`CHICKEN_TICKS_PER_PERCENT` is `DUEL_DECIDE_TICKS /
 * 100`), so an example that ran for a different length would be teaching a duel this game does not
 * have.
 */
export const DUEL_EXAMPLE_WINDOW_MS = (DUEL_DECIDE_TICKS / TICK_RATE_HZ) * 1000;

/**
 * How often the example advances its climb — one whole percentage point.
 *
 * The wire carries the pot as a whole percent, so a point is the smallest change the real bar can
 * ever show and there is nothing to be gained by redrawing between two of them. Ten renders a
 * second of one small card, which is what the live overlay costs while a duellist is reading it.
 */
export const DUEL_EXAMPLE_STEP_MS = DUEL_EXAMPLE_WINDOW_MS / MINI_GAME_POT_MAX_PERCENT;

/**
 * A duel for a mini-game page's example, held at one instant.
 *
 * **IT IS BUILT FOR WHATEVER GAME THE PAGE IS ABOUT AND NAMES NONE OF THEM.** `gameId`, the
 * buttons and the pot all come off the `MiniGame` handed in — the pot through `miniGamePotOf`,
 * which is the same registry lookup `GameConnection.duelView` uses one line along — so a third
 * mini-game gets a working example with no edit here, and a game with no pot gets a card with no
 * bar, which is exactly what its duel looks like. The string 'chicken' appears nowhere in it.
 *
 * The phase is `collecting` rather than `revealed` because the decision is the thing worth
 * teaching: a revealed card is a verdict, and a reader learns nothing from being shown one they
 * did not make. `endsAtMs` is a full window from the frozen instant the page took, so the
 * countdown is drawn at its start and stays there — `instructionStarVote`'s arrangement, and
 * `DuelPotBar`'s `settled` before it: this is a still picture and nothing is counting down.
 *
 * The pot sits at `instructionPotPercent` — the pot's own top rung — which is the frame that shows
 * the whole ladder reached and both risk bands either side of the climb.
 */
export function instructionDuel(
  game: MiniGame,
  startedAtMs: number,
  choice: string | null = null,
  percent = 0,
): DuelView {
  const spec = miniGamePotOf(game);
  const picked = game.choices.find((candidate) => candidate.id === choice) ?? null;
  return {
    id: 'd-example',
    gameId: game.id,
    phase: 'collecting',
    choices: game.choices,
    // The reader's own tap, on screen the instant it happens — there is no room to have heard it.
    // `youLocked` stays false, so the card reads "Sent…" rather than "In", which is the honest
    // state for a press that has left and not yet landed and is what it says in a real duel.
    yourChoice: picked,
    opponentChoice: null,
    youLocked: false,
    opponentLocked: false,
    opponentId: 'p-example',
    opponentName: INSTRUCTION_DUEL_OPPONENT,
    outcome: null,
    outcomeReason: null,
    stake: null,
    pot: spec === undefined ? null : { spec, percent },
    endsAtMs: startedAtMs + DUEL_EXAMPLE_WINDOW_MS,
  };
}
